import { useState, useEffect } from 'react';
import offlineDB from '../utils/offlineDB';

/**
 * Hook to manage pending signatures sync status
 */
export function useSyncStatus() {
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const updatePendingCount = async () => {
    try {
      const pending = await offlineDB.getPendingSignatures();
      setPendingCount(pending.length);
    } catch (error) {
      console.error('Failed to get pending signatures:', error);
    }
  };

  useEffect(() => {
    // Initial count
    updatePendingCount();

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SIGNATURE_SYNCED') {
          updatePendingCount();
        }
      });
    }

    // Poll for updates every 5 seconds
    const interval = setInterval(updatePendingCount, 5000);

    return () => clearInterval(interval);
  }, []);

  const triggerSync = async () => {
    setIsSyncing(true);
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        
        // Try background sync first
        if ('sync' in registration) {
          await registration.sync.register('sync-signatures');
        } else {
          // Fallback to manual sync
          const channel = new MessageChannel();
          registration.active.postMessage(
            { type: 'SYNC_NOW' },
            [channel.port2]
          );
          
          await new Promise((resolve, reject) => {
            channel.port1.onmessage = (event) => {
              if (event.data.success) {
                resolve();
              } else {
                reject(new Error(event.data.error));
              }
            };
          });
        }
      }
      await updatePendingCount();
    } catch (error) {
      console.error('Sync failed:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  };

  return { pendingCount, isSyncing, triggerSync, updatePendingCount };
}